import React from 'react';
import { usePermissions } from '@hooks/usePermissions';

// Props para o PermissionGuard
interface PermissionGuardProps {
  permission: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showError?: boolean;
}

// Props para o RoleGuard
interface RoleGuardProps {
  roles: string | string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireAll?: boolean; // true = AND (todas as roles), false = OR (qualquer role)
}

/**
 * Componente para proteger elementos baseado em permissões específicas
 * Conforme ERPApi/docs/seguranca/autorizacao.md
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  children,
  fallback = null,
  showError = false
}) => {
  const { checkPermission, isLoading, error } = usePermissions();

  // Aguardar carregamento da configuração
  if (isLoading) {
    return <div className="animate-pulse bg-slate-200 rounded h-4 w-16"></div>;
  }

  // Mostrar erro se configuração falhar
  if (error && showError) {
    console.error(`❌ PermissionGuard: Erro ao verificar permissão '${permission}':`, error);
    return (
      <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
        Erro ao carregar permissões: {error}
      </div>
    );
  }

  const result = checkPermission(permission);

  if (!result.hasPermission) {
    if (showError && result.reason) {
      return (
        <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
          {result.reason}
        </div>
      );
    }
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * Componente para proteger elementos baseado em roles específicas
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({
  roles,
  children,
  fallback = null,
  requireAll = false
}) => {
  const { hasRole, isLoading } = usePermissions();

  // Aguardar carregamento
  if (isLoading) {
    return <div className="animate-pulse bg-slate-200 rounded h-4 w-16"></div>;
  }

  const roleList = Array.isArray(roles) ? roles : [roles];
  
  const hasAccess = requireAll
    ? roleList.every(role => hasRole(role))  // AND: todas as roles
    : roleList.some(role => hasRole(role));  // OR: qualquer role

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * Higher-Order Component para proteger páginas inteiras
 */
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  permission: string,
  fallbackComponent?: React.ComponentType
) {
  const WrappedComponent = (props: P) => {
    const FallbackComponent = fallbackComponent || (() => (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl text-slate-300 mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-slate-700 mb-2">
            Acesso Restrito
          </h2>
          <p className="text-slate-500">
            Você não tem permissão para acessar esta página.
          </p>
        </div>
      </div>
    ));

    return (
      <PermissionGuard 
        permission={permission}
        fallback={<FallbackComponent />}
      >
        <Component {...props} />
      </PermissionGuard>
    );
  };

  WrappedComponent.displayName = `withPermission(${Component.displayName || Component.name})`;
  return WrappedComponent;
}

/**
 * Higher-Order Component para proteger por roles
 */
export function withRole<P extends object>(
  Component: React.ComponentType<P>,
  roles: string | string[],
  requireAll: boolean = false,
  fallbackComponent?: React.ComponentType
) {
  const WrappedComponent = (props: P) => {
    const FallbackComponent = fallbackComponent || (() => (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl text-slate-300 mb-4">👮‍♂️</div>
          <h2 className="text-xl font-semibold text-slate-700 mb-2">
            Acesso Negado
          </h2>
          <p className="text-slate-500">
            Sua role não permite acesso a esta funcionalidade.
          </p>
        </div>
      </div>
    ));

    return (
      <RoleGuard 
        roles={roles}
        requireAll={requireAll}
        fallback={<FallbackComponent />}
      >
        <Component {...props} />
      </RoleGuard>
    );
  };

  WrappedComponent.displayName = `withRole(${Component.displayName || Component.name})`;
  return WrappedComponent;
}

// Re-export hook para conveniência
export { usePermissions } from '@hooks/usePermissions';